import { createSlice, createEntityAdapter, PayloadAction } from '@reduxjs/toolkit';
import type { Challenge } from '@ai-platform/database';
import type { Course } from '@ai-platform/utils';
import { PromptDto } from '../../../components/types';
import { Slices } from '../slices';
import { PromptsSchema, FeedCache, MyPromptsFeedCache, UserPromptsFeedCache } from './types';

/**
 * EntityAdapter для нормализованного хранения промптов
 */
export const promptsAdapter = createEntityAdapter<PromptDto, number>({
    selectId: prompt => prompt.id,
    sortComparer: (a, b) => {
        // Сортировка по дате создания (новые сначала)
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    },
});

const initialState: PromptsSchema = promptsAdapter.getInitialState({
    loading: false,
    error: null,
    lists: {
        popular: [],
        similar: {},
        main: {},
        courses: [],
        challenges: [],
        my: null,
        users: {},
    },
});

/**
 * Slice для управления промптами
 */
const promptsSlice = createSlice({
    name: Slices.Prompts,
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },

        /**
         * Добавить промпты в кеш
         */
        addPrompts: (state, action: PayloadAction<PromptDto[]>) => {
            promptsAdapter.upsertMany(state, action.payload);
        },

        /**
         * Обновить промпт в кеше
         */
        updatePrompt: (state, action: PayloadAction<{ id: number; changes: Partial<PromptDto> }>) => {
            promptsAdapter.updateOne(state, action.payload);
        },

        /**
         * Обновить лайки промпта
         */
        updatePromptLikes: (state, action: PayloadAction<{ id: number; likes: number; isLiked: boolean }>) => {
            const { id, likes, isLiked } = action.payload;
            promptsAdapter.updateOne(state, {
                id,
                changes: { likes, isLiked },
            });
        },

        setPopularPrompts: (state, action: PayloadAction<PromptDto[]>) => {
            promptsAdapter.upsertMany(state, action.payload);
            state.lists.popular = action.payload.map(p => p.id);
        },

        setSimilarPrompts: (state, action: PayloadAction<{ promptId: number; prompts: PromptDto[] }>) => {
            const { promptId, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);
            state.lists.similar[promptId] = prompts.map(p => p.id);
        },

        setFeedCache: (
            state,
            action: PayloadAction<{
                key: string;
                cache: FeedCache;
                prompts: PromptDto[];
            }>,
        ) => {
            const { key, cache, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);
            state.lists.main[key] = cache;
        },

        appendToFeedCache: (
            state,
            action: PayloadAction<{
                key: string;
                promptIds: number[];
                nextCursor: string | null;
                hasMorePrompts: boolean;
                prompts: PromptDto[];
            }>,
        ) => {
            const { key, promptIds, nextCursor, hasMorePrompts, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);

            const existingCache = state.lists.main[key];
            if (existingCache) {
                existingCache.promptIds.push(...promptIds);
                existingCache.nextCursor = nextCursor;
                existingCache.hasMorePrompts = hasMorePrompts;
            }
        },

        clearFeedCache: (state, action: PayloadAction<string>) => {
            const key = action.payload;
            delete state.lists.main[key];
        },

        setCourses: (state, action: PayloadAction<Course[]>) => {
            state.lists.courses = action.payload;
        },

        setChallenges: (state, action: PayloadAction<Challenge[]>) => {
            state.lists.challenges = action.payload;
        },

        setMyFeedCache: (
            state,
            action: PayloadAction<{
                cache: MyPromptsFeedCache;
                prompts: PromptDto[];
            }>,
        ) => {
            const { cache, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);
            state.lists.my = cache;
        },

        appendToMyFeedCache: (
            state,
            action: PayloadAction<{
                promptIds: number[];
                nextCursor: string | null;
                hasMore: boolean;
                prompts: PromptDto[];
            }>,
        ) => {
            const { promptIds, nextCursor, hasMore, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);

            if (state.lists.my) {
                const existingIds = new Set(state.lists.my.promptIds);
                const uniqueNewIds = promptIds.filter(id => !existingIds.has(id));

                state.lists.my.promptIds.push(...uniqueNewIds);
                state.lists.my.nextCursor = nextCursor;
                state.lists.my.hasMore = hasMore;
            }
        },

        clearMyFeedCache: state => {
            state.lists.my = null;
        },

        setUserFeedCache: (
            state,
            action: PayloadAction<{
                publicId: string;
                cache: UserPromptsFeedCache;
                prompts: PromptDto[];
            }>,
        ) => {
            const { publicId, cache, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);
            state.lists.users[publicId] = cache;
        },

        appendToUserFeedCache: (
            state,
            action: PayloadAction<{
                publicId: string;
                promptIds: number[];
                nextCursor: string | null;
                hasMore: boolean;
                prompts: PromptDto[];
            }>,
        ) => {
            const { publicId, promptIds, nextCursor, hasMore, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);

            const existingCache = state.lists.users[publicId];
            if (existingCache) {
                const existingIds = new Set(existingCache.promptIds);
                const uniqueNewIds = promptIds.filter(id => !existingIds.has(id));

                existingCache.promptIds.push(...uniqueNewIds);
                existingCache.nextCursor = nextCursor;
                existingCache.hasMore = hasMore;
            }
        },

        clearCache: state => {
            promptsAdapter.removeAll(state);
            state.lists = {
                popular: [],
                similar: {},
                main: {},
                courses: [],
                challenges: [],
                my: null,
                users: {},
            };
            state.error = null;
        },
    },
});

export const promptsActions = promptsSlice.actions;
export const promptsReducer = promptsSlice.reducer;
